import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useFamily } from '@/contexts/FamilyContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  ChevronRight,
  Droplets,
  FileText,
  Heart,
  Pill,
  Phone,
  Plus,
  RefreshCw,
  Ruler,
  Save,
  Scale,
  Syringe,
  User,
  X,
} from 'lucide-react';
import { medicalService, getLocalizedValue } from '@/services/api';

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const alcoholOptions = ['none', 'occasional', 'moderate', 'heavy'];
const exerciseOptions = ['none', 'rarely', 'weekly', 'daily'];

const createEmptyMedicalData = () => ({
  name: '',
  phone: '',
  email: '',
  gender: '',
  dateOfBirth: '',
  bloodGroup: '',
  height: '',
  weight: '',
  smoker: false,
  smokingYears: '',
  alcoholConsumption: 'none',
  exerciseFrequency: 'none',
  familyHistory: '',
  chronicDiseases: [],
  permanentMedications: [],
  allergies: [],
  surgeries: [],
  vaccines: [],
});

const createEmptyTagInputs = () => ({
  chronicDiseases: '',
  permanentMedications: '',
  allergies: '',
  surgeries: '',
  vaccines: '',
});

const createEmptyOptions = () => ({
  chronicDiseases: [],
  permanentMedications: [],
  allergies: [],
  surgeries: [],
  vaccines: [],
});

const sectionStyles = {
  chronicDiseases: {
    icon: Heart,
    iconClassName: 'text-red-500',
    badgeClassName: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  },
  permanentMedications: {
    icon: Pill,
    iconClassName: 'text-blue-500',
    badgeClassName: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
  allergies: {
    icon: AlertTriangle,
    iconClassName: 'text-amber-500',
    badgeClassName: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  surgeries: {
    icon: Activity,
    iconClassName: 'text-orange-500',
    badgeClassName: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  },
  vaccines: {
    icon: Syringe,
    iconClassName: 'text-green-500',
    badgeClassName: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  },
};

const readMap = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  return {};
};

const readList = (value) => (Array.isArray(value) ? value : []);

const readString = (value) => {
  if (value == null || value === false) {
    return '';
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    for (const key of ['name', 'display_name', 'en', 'ar', 'value']) {
      const resolved = readString(value[key]);
      if (resolved) {
        return resolved;
      }
    }
    return '';
  }

  return String(value).trim();
};

const readBool = (value, fallback = false) => {
  if (value == null) {
    return fallback;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) {
    return true;
  }
  if (['false', '0', 'no', 'n', ''].includes(normalized)) {
    return false;
  }
  return fallback;
};

const readNumberString = (value) => {
  if (value == null || value === '') {
    return '';
  }

  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed === 0) {
    return '';
  }

  return Number.isInteger(parsed) ? String(parsed) : String(parsed);
};

const readWrappedData = (raw) => {
  const payload = readMap(raw);
  const wrappedData = readMap(payload.data);
  return Object.keys(wrappedData).length > 0 ? wrappedData : payload;
};

const extractOptionNames = (raw) => {
  const data = readWrappedData(raw);
  const source = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];

  return source
    .map((item) => readString(readMap(item).name || item))
    .filter(Boolean);
};

const extractNamedItems = (raw) =>
  readList(raw)
    .map((item) => readString(readMap(item).name || item))
    .filter(Boolean);

const normalizeChoice = (value, options, fallback) =>
  options.includes(value) ? value : fallback;

const getPatientId = (patient) =>
  Number(patient?.numericId || patient?.id || patient?.patient_id || patient?.partner_id || 0) || 0;

const getPatientInitials = (patient) => {
  const name = readString(patient?.name);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2);

  return initials || 'P';
};

const toListPayload = (items) =>
  items
    .map((item) => readString(item))
    .filter(Boolean)
    .map((name) => ({ name }));

const mergeUniqueItems = (items, nextValue) => {
  const normalizedValue = readString(nextValue);
  if (!normalizedValue) {
    return items;
  }

  const exists = items.some(
    (item) => item.trim().toLowerCase() === normalizedValue.trim().toLowerCase()
  );
  if (exists) {
    return items;
  }

  return [...items, normalizedValue];
};

const labels = {
  ar: {
    choosePatient: 'اختر المريض',
    choosePatientSubtitle: 'اختر المريض الذي تريد فتح سجله الطبي',
    selectedPatient: 'المريض المحدد',
    changePatient: 'تغيير المريض',
    defaultPatient: 'المريض الافتراضي',
    noPatientsTitle: 'لا توجد ملفات مرضى',
    noPatientsBody: 'أضف مريضاً أولاً حتى تتمكن من فتح السجل الطبي.',
    managePatients: 'إدارة المرضى',
    cancel: 'إلغاء',
    selectPatientFirst: 'يرجى اختيار المريض أولاً',
    title: 'السجل الطبي',
    subtitle: 'معلوماتك الصحية الهامة',
    patientInfo: 'معلومات المريض',
    basicMedicalInfo: 'البيانات الطبية',
    fullName: 'الاسم الكامل',
    phone: 'رقم الهاتف',
    email: 'البريد الإلكتروني',
    gender: 'الجنس',
    dateOfBirth: 'تاريخ الميلاد',
    bloodType: 'زمرة الدم',
    selectBloodType: 'اختر زمرة الدم',
    height: 'الطول',
    weight: 'الوزن',
    smoker: 'مدخن',
    smokingYears: 'سنوات التدخين',
    alcohol: 'الكحول',
    exercise: 'الرياضة',
    note: 'ملاحظة',
    notePlaceholder: 'ملاحظة',
    save: 'حفظ',
    saving: 'جارٍ الحفظ...',
    chooseSavedOptions: 'اختر من الخيارات المحفوظة',
    add: 'إضافة',
    noData: 'لا توجد بيانات',
    chronicDiseases: 'الأمراض المزمنة',
    permanentMedications: 'دواء دائم',
    allergies: 'حساسية الأدوية',
    surgeries: 'العمليات الجراحية',
    vaccines: 'اللقاحات',
    addChronicDisease: 'أضف مرضًا مزمنًا',
    addPermanentMedication: 'أضف دواءً دائمًا',
    addAllergy: 'أضف حساسية دواء',
    addSurgery: 'أضف عملية',
    addVaccine: 'أضف لقاحًا',
    medicalSaved: 'تم حفظ السجل الطبي بنجاح',
    medicalSaveError: 'فشل في حفظ السجل الطبي',
    noSavedOptions: 'لا توجد خيارات محفوظة',
    noChronicDiseases: 'لا توجد أمراض مزمنة',
    noPermanentMedications: 'لا توجد أدوية دائمة',
    noAllergies: 'لا توجد حساسيات أدوية',
    noSurgeries: 'لا توجد عمليات جراحية',
    noVaccines: 'لا توجد لقاحات',
    male: 'ذكر',
    female: 'أنثى',
    other: 'آخر',
    none: 'لا يوجد',
    occasional: 'أحيانًا',
    moderate: 'متوسط',
    heavy: 'كثير',
    rarely: 'نادرًا',
    weekly: 'أسبوعيًا',
    daily: 'يوميًا',
  },
  en: {
    choosePatient: 'Choose patient',
    choosePatientSubtitle: 'Select the patient whose medical record you want to open',
    selectedPatient: 'Selected patient',
    changePatient: 'Change patient',
    defaultPatient: 'Default Patient',
    noPatientsTitle: 'No patient profiles found',
    noPatientsBody: 'Add a patient profile first to open a medical record.',
    managePatients: 'Manage patients',
    cancel: 'Cancel',
    selectPatientFirst: 'Please choose a patient first',
    title: 'Medical Record',
    subtitle: 'Your important health information',
    patientInfo: 'Patient Information',
    basicMedicalInfo: 'Medical Information',
    fullName: 'Full Name',
    phone: 'Phone Number',
    email: 'Email',
    gender: 'Gender',
    dateOfBirth: 'Date of Birth',
    bloodType: 'Blood Type',
    selectBloodType: 'Select blood type',
    height: 'Height',
    weight: 'Weight',
    smoker: 'Smoker',
    smokingYears: 'Years',
    alcohol: 'Alcohol',
    exercise: 'Exercise',
    note: 'Note',
    notePlaceholder: 'Note',
    save: 'Save',
    saving: 'Saving...',
    chooseSavedOptions: 'Choose from saved options',
    add: 'Add',
    noData: 'No data available',
    chronicDiseases: 'Chronic disease',
    permanentMedications: 'permanent medication',
    allergies: 'Drug allergies',
    surgeries: 'Surgical operations',
    vaccines: 'vaccines',
    addChronicDisease: 'Add chronic disease',
    addPermanentMedication: 'Add permanent medication',
    addAllergy: 'Add drug allergy',
    addSurgery: 'Add surgery',
    addVaccine: 'Add vaccine',
    medicalSaved: 'Medical record saved successfully',
    medicalSaveError: 'Failed to save medical record',
    noSavedOptions: 'No saved options found',
    noChronicDiseases: 'No chronic diseases recorded',
    noPermanentMedications: 'No permanent medications recorded',
    noAllergies: 'No drug allergies recorded',
    noSurgeries: 'No surgeries recorded',
    noVaccines: 'No vaccines recorded',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    none: 'None',
    occasional: 'Occasional',
    moderate: 'Moderate',
    heavy: 'Heavy',
    rarely: 'Rarely',
    weekly: 'Weekly',
    daily: 'Daily',
  },
};

const MedicalRecordsPage = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { familyMembers, loading: familyLoading } = useFamily();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const copy = labels[lang];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [medicalData, setMedicalData] = useState(createEmptyMedicalData);
  const [medicalOptions, setMedicalOptions] = useState(createEmptyOptions);
  const [tagInputs, setTagInputs] = useState(createEmptyTagInputs);
  const selectedPatientId = getPatientId(selectedPatient);
  const patientIdParam = Number(searchParams.get('patientId') || 0) || 0;
  const selectablePatients = useMemo(
    () => familyMembers.filter((member) => getPatientId(member) > 0),
    [familyMembers]
  );

  const setFieldValue = (field, value) => {
    setMedicalData((prev) => ({ ...prev, [field]: value }));
  };

  const loadMedicalOptionsFallback = useCallback(async () => {
    try {
      const responses = await Promise.all([
        medicalService.getChronicDiseases(),
        medicalService.getMedications(),
        medicalService.getAllergies(),
        medicalService.getSurgeries(),
        medicalService.getVaccines(),
      ]);

      setMedicalOptions({
        chronicDiseases: extractOptionNames(responses[0]?.data),
        permanentMedications: extractOptionNames(responses[1]?.data),
        allergies: extractOptionNames(responses[2]?.data),
        surgeries: extractOptionNames(responses[3]?.data),
        vaccines: extractOptionNames(responses[4]?.data),
      });
    } catch (error) {
      console.error('Failed to load medical options:', error);
      setMedicalOptions(createEmptyOptions());
    }
  }, []);

  const loadMedicalOptions = useCallback(async () => {
    try {
      const response = await medicalService.getHistoryOptions();
      const data = readWrappedData(response?.data);

      const resolved = {
        chronicDiseases: extractOptionNames(data.chronic_diseases),
        permanentMedications: extractOptionNames(data.permanent_medications),
        allergies: extractOptionNames(data.allergies),
        surgeries: extractOptionNames(data.surgeries),
        vaccines: extractOptionNames(data.vaccines),
      };

      const allEmpty = Object.values(resolved).every((items) => items.length === 0);
      if (allEmpty) {
        await loadMedicalOptionsFallback();
        return;
      }

      setMedicalOptions(resolved);
    } catch (error) {
      await loadMedicalOptionsFallback();
    }
  }, [loadMedicalOptionsFallback]);

  useEffect(() => {
    if (!patientIdParam) {
      return;
    }

    if (selectedPatientId === patientIdParam) {
      return;
    }

    const matchingPatient = selectablePatients.find(
      (patient) => getPatientId(patient) === patientIdParam
    );

    if (matchingPatient) {
      setSelectedPatient(matchingPatient);
      setPickerOpen(false);
      return;
    }

    if (!familyLoading) {
      setSelectedPatient({
        id: String(patientIdParam),
        numericId: patientIdParam,
        name: `${copy.patientInfo} #${patientIdParam}`,
      });
      setPickerOpen(false);
    }
  }, [
    copy.patientInfo,
    familyLoading,
    patientIdParam,
    selectablePatients,
    selectedPatientId,
  ]);

  const hydrateMedicalRecord = useCallback((data) => {
    const patient = readMap(data.patient);
    const medicalHistory = readMap(data.medical_history);
    const fallbackPatient = readMap(selectedPatient || user?.patient || {});

    setMedicalData((prev) => ({
      ...prev,
      name: readString(patient.name) || readString(fallbackPatient.name) || readString(user?.name),
      phone:
        readString(patient.phone || patient.phone_number) ||
        readString(fallbackPatient.phone || fallbackPatient.phone_number) ||
        readString(user?.phone || user?.phone_number) ||
        '',
      email:
        readString(patient.email) ||
        readString(fallbackPatient.email) ||
        readString(user?.email) ||
        '',
      gender: readString(patient.gender || fallbackPatient.gender),
      dateOfBirth: readString(patient.date_of_birth || fallbackPatient.date_of_birth),
      bloodGroup: readString(patient.blood_type || medicalHistory.blood_type),
      height: readNumberString(patient.height || medicalHistory.height),
      weight: readNumberString(patient.weight || medicalHistory.weight),
      smoker: readBool(medicalHistory.is_smoker),
      smokingYears: readNumberString(medicalHistory.smoking_years),
      alcoholConsumption: normalizeChoice(
        readString(medicalHistory.alcohol_consumption),
        alcoholOptions,
        'none'
      ),
      exerciseFrequency: normalizeChoice(
        readString(medicalHistory.exercise_frequency),
        exerciseOptions,
        'none'
      ),
      familyHistory: readString(medicalHistory.family_history),
      chronicDiseases: extractNamedItems(medicalHistory.chronic_diseases),
      permanentMedications: extractNamedItems(medicalHistory.permanent_medications),
      allergies: extractNamedItems(medicalHistory.allergies),
      surgeries: extractNamedItems(medicalHistory.surgeries),
      vaccines: extractNamedItems(medicalHistory.vaccines),
    }));
  }, [selectedPatient, user]);

  const loadMedicalRecord = useCallback(async (patientId = selectedPatientId) => {
    if (!patientId) {
      return;
    }

    try {
      const response = await medicalService.getPatientMedicalRecord(patientId);
      hydrateMedicalRecord(readWrappedData(response?.data));
    } catch (error) {
      console.error('Failed to load medical history:', error);
    }
  }, [
    hydrateMedicalRecord,
    selectedPatientId,
  ]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!selectedPatientId) {
      setMedicalData(createEmptyMedicalData());
      setTagInputs(createEmptyTagInputs());
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([loadMedicalOptions(), loadMedicalRecord(selectedPatientId)]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    authLoading,
    isAuthenticated,
    loadMedicalOptions,
    loadMedicalRecord,
    navigate,
    selectedPatientId,
  ]);

  const addTag = (sectionKey, rawValue) => {
    const value = readString(rawValue);
    if (!value) {
      return;
    }

    setMedicalData((prev) => ({
      ...prev,
      [sectionKey]: mergeUniqueItems(prev[sectionKey], value),
    }));
    setTagInputs((prev) => ({ ...prev, [sectionKey]: '' }));
  };

  const removeTag = (sectionKey, value) => {
    setMedicalData((prev) => ({
      ...prev,
      [sectionKey]: prev[sectionKey].filter(
        (item) => item.trim().toLowerCase() !== String(value).trim().toLowerCase()
      ),
    }));
  };

  const buildPayload = () => ({
    name: readString(medicalData.name),
    phone: readString(medicalData.phone),
    email: readString(medicalData.email),
    gender: readString(medicalData.gender),
    date_of_birth: readString(medicalData.dateOfBirth),
    blood_type: readString(medicalData.bloodGroup),
    height: Number(medicalData.height || 0),
    weight: Number(medicalData.weight || 0),
    chronic_diseases: toListPayload(medicalData.chronicDiseases),
    permanent_medications: toListPayload(medicalData.permanentMedications),
    allergies: toListPayload(medicalData.allergies),
    surgeries: toListPayload(medicalData.surgeries),
    vaccines: toListPayload(medicalData.vaccines),
    is_smoker: !!medicalData.smoker,
    smoking_years: Number(medicalData.smokingYears || 0),
    alcohol_consumption: readString(medicalData.alcoholConsumption) || 'none',
    exercise_frequency: readString(medicalData.exerciseFrequency) || 'none',
    family_history: readString(medicalData.familyHistory),
  });

  const handleSave = async () => {
    if (!selectedPatientId) {
      toast.error(copy.selectPatientFirst);
      setPickerOpen(true);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();

      await medicalService.updatePatientMedicalRecord(selectedPatientId, payload);

      toast.success(copy.medicalSaved);
      await loadMedicalRecord(selectedPatientId);
    } catch (error) {
      console.error('Failed to save medical record:', error);
      toast.error(copy.medicalSaveError);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectPatient = (patient) => {
    const patientId = getPatientId(patient);
    if (!patientId) {
      return;
    }

    setSelectedPatient(patient);
    setMedicalData(createEmptyMedicalData());
    setTagInputs(createEmptyTagInputs());
    setSearchParams({ patientId: String(patientId) });
    setPickerOpen(false);
  };

  const renderPatientPicker = () => {
    const showPicker = pickerOpen || !selectedPatientId;
    if (!showPicker) {
      return null;
    }

    return (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 px-0 sm:items-center sm:px-4"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-full max-w-2xl rounded-t-3xl bg-white p-5 shadow-2xl dark:bg-slate-900 sm:rounded-2xl">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                {copy.choosePatient}
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {copy.choosePatientSubtitle}
              </p>
            </div>
            {pickerOpen && selectedPatientId ? (
              <Button variant="ghost" size="sm" onClick={() => setPickerOpen(false)}>
                {copy.cancel}
              </Button>
            ) : null}
          </div>

          {familyLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <Skeleton key={item} className="h-20 w-full rounded-2xl" />
              ))}
            </div>
          ) : selectablePatients.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 p-5 text-center dark:border-slate-700">
              <p className="font-semibold text-slate-900 dark:text-white">{copy.noPatientsTitle}</p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{copy.noPatientsBody}</p>
              <Button className="mt-4" onClick={() => navigate('/family')}>
                {copy.managePatients}
              </Button>
            </div>
          ) : (
            <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
              {selectablePatients.map((patient) => {
                const patientId = getPatientId(patient);
                const subtitleParts = [
                  patient.isDefault ? copy.defaultPatient : '',
                  readString(patient.phone),
                ].filter(Boolean);

                return (
                  <button
                    key={patientId}
                    type="button"
                    onClick={() => handleSelectPatient(patient)}
                    className="flex w-full items-center gap-4 rounded-2xl border border-indigo-100 bg-white p-4 text-start shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50/40 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
                  >
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-teal-100 text-lg font-bold text-teal-700 dark:bg-teal-900/40 dark:text-teal-200">
                      {getPatientInitials(patient)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-lg font-bold text-indigo-700 dark:text-indigo-300">
                        {patient.name}
                      </div>
                      {subtitleParts.length > 0 ? (
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                          {patient.isDefault ? <span>{copy.defaultPatient}</span> : null}
                          {readString(patient.phone) ? (
                            <span className="inline-flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {patient.phone}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <ChevronRight className={`h-5 w-5 text-indigo-500 ${lang === 'ar' ? 'rotate-180' : ''}`} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTagSection = ({
    keyName,
    title,
    placeholder,
    emptyText,
    suggestions = [],
  }) => {
    const style = sectionStyles[keyName];
    const Icon = style.icon;
    const items = medicalData[keyName];
    const inputValue = tagInputs[keyName];
    const availableSuggestions = suggestions.filter(
      (option) =>
        option.trim() &&
        !items.some((item) => item.trim().toLowerCase() === option.trim().toLowerCase())
    );

    return (
      <Card className="dark:bg-slate-800 border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Icon className={`h-5 w-5 ${style.iconClassName}`} />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(event) =>
                setTagInputs((prev) => ({ ...prev, [keyName]: event.target.value }))
              }
              placeholder={placeholder}
              className="dark:bg-slate-700 dark:text-white"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTag(keyName, inputValue);
                }
              }}
            />
            <Button onClick={() => addTag(keyName, inputValue)} variant="outline" size="icon">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <Select onValueChange={(value) => addTag(keyName, value)}>
            <SelectTrigger className="dark:bg-slate-700 dark:text-white">
              <SelectValue
                placeholder={
                  availableSuggestions.length > 0 ? copy.chooseSavedOptions : copy.noSavedOptions
                }
              />
            </SelectTrigger>
            <SelectContent>
              {availableSuggestions.length > 0 ? (
                availableSuggestions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {getLocalizedValue(option, lang) || option}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="__no_saved_options__" disabled>
                  {copy.noSavedOptions}
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          <div className="flex flex-wrap gap-2">
            {items.map((item) => (
              <Badge
                key={`${keyName}-${item}`}
                variant="secondary"
                className={`flex items-center gap-1 px-3 py-1 ${style.badgeClassName}`}
              >
                {item}
                <button type="button" onClick={() => removeTag(keyName, item)}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {items.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">{emptyText}</p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="medical-records-page">
      <div className="bg-gradient-to-br from-rose-600 to-rose-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">{copy.title}</h1>
              <p className="text-rose-100 text-sm">{copy.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      {renderPatientPicker()}

      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((item) => (
              <Skeleton key={item} className="h-32 w-full" />
            ))}
          </div>
        ) : !selectedPatientId ? (
          <Card className="border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="p-8 text-center">
              <User className="mx-auto mb-4 h-12 w-12 text-rose-300" />
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                {copy.choosePatient}
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
                {copy.choosePatientSubtitle}
              </p>
              <Button className="mt-5 bg-rose-600 hover:bg-rose-700" onClick={() => setPickerOpen(true)}>
                {copy.choosePatient}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm dark:bg-slate-800">
              <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-100 text-lg font-bold text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
                    {getPatientInitials(selectedPatient)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{copy.selectedPatient}</p>
                    <p className="truncate text-lg font-bold text-slate-900 dark:text-white">
                      {selectedPatient?.name || medicalData.name}
                    </p>
                    {selectedPatient?.phone ? (
                      <p className="mt-1 inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                        <Phone className="h-3.5 w-3.5" />
                        {selectedPatient.phone}
                      </p>
                    ) : null}
                  </div>
                </div>
                <Button variant="outline" onClick={() => setPickerOpen(true)}>
                  {copy.changePatient}
                </Button>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <User className="h-5 w-5 text-rose-600" />
                  {copy.patientInfo}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label className="dark:text-slate-300">{copy.fullName}</Label>
                  <Input
                    value={medicalData.name}
                    onChange={(event) => setFieldValue('name', event.target.value)}
                    className="dark:bg-slate-700 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">{copy.phone}</Label>
                  <Input
                    value={medicalData.phone}
                    readOnly
                    className="dark:bg-slate-700 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">{copy.email}</Label>
                  <Input
                    value={medicalData.email}
                    readOnly
                    className="dark:bg-slate-700 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">{copy.gender}</Label>
                  <Select
                    value={medicalData.gender || undefined}
                    onValueChange={(value) => setFieldValue('gender', value)}
                  >
                    <SelectTrigger className="dark:bg-slate-700 dark:text-white">
                      <SelectValue placeholder={copy.gender} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{copy.male}</SelectItem>
                      <SelectItem value="female">{copy.female}</SelectItem>
                      <SelectItem value="other">{copy.other}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300">{copy.dateOfBirth}</Label>
                  <Input
                    type="date"
                    value={medicalData.dateOfBirth}
                    onChange={(event) => setFieldValue('dateOfBirth', event.target.value)}
                    className="dark:bg-slate-700 dark:text-white"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Droplets className="h-5 w-5 text-red-500" />
                  {copy.basicMedicalInfo}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 dark:text-slate-300">
                    <Droplets className="h-4 w-4" />
                    {copy.bloodType}
                  </Label>
                  <Select
                    value={medicalData.bloodGroup || undefined}
                    onValueChange={(value) => setFieldValue('bloodGroup', value)}
                  >
                    <SelectTrigger className="dark:bg-slate-700 dark:text-white">
                      <SelectValue placeholder={copy.selectBloodType} />
                    </SelectTrigger>
                    <SelectContent>
                      {bloodGroups.map((group) => (
                        <SelectItem key={group} value={group}>
                          {group}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 dark:text-slate-300">
                    <Ruler className="h-4 w-4" />
                    {copy.height}
                  </Label>
                  <Input
                    type="number"
                    value={medicalData.height}
                    onChange={(event) => setFieldValue('height', event.target.value)}
                    placeholder="180"
                    className="dark:bg-slate-700 dark:text-white"
                    data-testid="height-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 dark:text-slate-300">
                    <Scale className="h-4 w-4" />
                    {copy.weight}
                  </Label>
                  <Input
                    type="number"
                    value={medicalData.weight}
                    onChange={(event) => setFieldValue('weight', event.target.value)}
                    placeholder="60"
                    className="dark:bg-slate-700 dark:text-white"
                    data-testid="weight-input"
                  />
                </div>
              </CardContent>
            </Card>

            {renderTagSection({
              keyName: 'chronicDiseases',
              title: copy.chronicDiseases,
              placeholder: copy.addChronicDisease,
              emptyText: copy.noChronicDiseases,
              suggestions: medicalOptions.chronicDiseases,
            })}

            {renderTagSection({
              keyName: 'permanentMedications',
              title: copy.permanentMedications,
              placeholder: copy.addPermanentMedication,
              emptyText: copy.noPermanentMedications,
              suggestions: medicalOptions.permanentMedications,
            })}

            {renderTagSection({
              keyName: 'allergies',
              title: copy.allergies,
              placeholder: copy.addAllergy,
              emptyText: copy.noAllergies,
              suggestions: medicalOptions.allergies,
            })}

            {renderTagSection({
              keyName: 'surgeries',
              title: copy.surgeries,
              placeholder: copy.addSurgery,
              emptyText: copy.noSurgeries,
              suggestions: medicalOptions.surgeries,
            })}

            {renderTagSection({
              keyName: 'vaccines',
              title: copy.vaccines,
              placeholder: copy.addVaccine,
              emptyText: copy.noVaccines,
              suggestions: medicalOptions.vaccines,
            })}

            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <Activity className="h-5 w-5 text-slate-500" />
                  {lang === 'ar' ? 'نمط الحياة' : 'Lifestyle'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                  <Checkbox
                    id="medical-smoker"
                    checked={medicalData.smoker}
                    onCheckedChange={(checked) => setFieldValue('smoker', !!checked)}
                  />
                  <Label htmlFor="medical-smoker" className="cursor-pointer dark:text-slate-300">
                    {copy.smoker}
                  </Label>
                </div>

                {medicalData.smoker ? (
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">{copy.smokingYears}</Label>
                    <Input
                      type="number"
                      value={medicalData.smokingYears}
                      onChange={(event) => setFieldValue('smokingYears', event.target.value)}
                      placeholder="5"
                      className="dark:bg-slate-700 dark:text-white"
                    />
                  </div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">{copy.alcohol}</Label>
                    <Select
                      value={medicalData.alcoholConsumption}
                      onValueChange={(value) => setFieldValue('alcoholConsumption', value)}
                    >
                      <SelectTrigger className="dark:bg-slate-700 dark:text-white">
                        <SelectValue placeholder={copy.alcohol} />
                      </SelectTrigger>
                      <SelectContent>
                        {alcoholOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {copy[option]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="dark:text-slate-300">{copy.exercise}</Label>
                    <Select
                      value={medicalData.exerciseFrequency}
                      onValueChange={(value) => setFieldValue('exerciseFrequency', value)}
                    >
                      <SelectTrigger className="dark:bg-slate-700 dark:text-white">
                        <SelectValue placeholder={copy.exercise} />
                      </SelectTrigger>
                      <SelectContent>
                        {exerciseOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {copy[option]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 dark:text-white">
                  <FileText className="h-5 w-5 text-teal-500" />
                  {copy.note}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={medicalData.familyHistory}
                  onChange={(event) => setFieldValue('familyHistory', event.target.value)}
                  placeholder={copy.notePlaceholder}
                  className="min-h-[120px] dark:bg-slate-700 dark:text-white"
                  data-testid="notes-textarea"
                />
              </CardContent>
            </Card>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white py-6 text-lg"
              data-testid="save-medical-record-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  {copy.saving}
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  {copy.save}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicalRecordsPage;
